import { alpha } from "@mui/material";

//================================================

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// GREY legado — mantido porque overrides e telas antigas usam grey[500_xx]
const GREY = {
  0: "#FFFFFF",
  100: "#F9FAFB",
  200: "#F4F6F8",
  300: "#DFE3E8",
  400: "#C4CDD5",
  500: "#919EAB",
  600: "#637381",
  700: "#454F5B",
  800: "#212B36",
  900: "#161C24",
  500_8: alpha("#919EAB", 0.08),
  500_12: alpha("#919EAB", 0.12),
  500_16: alpha("#919EAB", 0.16),
  500_24: alpha("#919EAB", 0.24),
  500_32: alpha("#919EAB", 0.32),
  500_48: alpha("#919EAB", 0.48),
  500_56: alpha("#919EAB", 0.56),
  500_80: alpha("#919EAB", 0.8),
};

const GOLD = "#C0985D";
const GOLD_DARKMODE = "#CBA56A";

const GRADIENTS = {
  primary: createGradient("#D1BB8E", GOLD),
  info: createGradient("#d1bb8e", "#d1bb8e"),
  success: createGradient("#7FC295", "#54A86B"),
  warning: createGradient("#D8B468", "#B6912E"),
  error: createGradient("#FFA48D", "#FF4842"),
};

export type GradientsPalette = typeof GRADIENTS;

/** Tokens de marca do handoff que não cabem no shape padrão do MUI */
export interface BrandPalette {
  /** Fundo da sidebar do app (gradiente no claro, sólido no escuro) */
  sidebarApp: string;
  sidebarBorder: string;
  /** Fundo da sidebar do admin (gradiente espresso) */
  sidebarAdmin: string;
  /** Painel das telas de autenticação (marrom no claro, café escuro no escuro) */
  authPanel: string;
  /** Gradiente do card "Seu plano"/hero (escuro nos dois temas) */
  heroCard: string;
  heroCardBorder: string;
  cream: string;
  creamStroke: string;
  /** Superfície 2 (linhas alternadas, hover sutil) */
  surface2: string;
  surfaceHover: string;
  hexTileBg: string;
  hexTileStroke: string;
  navActiveBg: string;
  navActiveText: string;
  inputFocusHalo: string;
  successBg: string;
  warningBg: string;
  errorBg: string;
  whatsapp: string;
  /** Fundo translúcido da topbar (blur por cima) */
  topbar: string;
  /** Switcher Plataforma|Admin da sidebar */
  switcherBg: string;
  switcherActiveBg: string;
  switcherActiveText: string;
  /** Labels de grupo da sidebar (PRINCIPAL/CONTA) */
  navGroupLabel: string;
  /** Texto de item de nav não-ativo (sidebar é colorida nos 2 modos) */
  navItemText: string;
  navHoverBg: string;
  /** Badge "IA" (Cléo) */
  badgeIaBg: string;
  badgeIaText: string;
  /** Hero do dashboard (gradiente creme claro / marrom escuro) */
  heroBg: string;
  heroBorder: string;
  heroText: string;
  /** Banner do hero no mobile (gradiente dourado, texto branco) */
  heroMobile: string;
  /** Stepper do wizard: dot concluído */
  stepDoneBg: string;
  stepDoneText: string;
  /** Trilho de switch desligado */
  toggleOff: string;
}

const LIGHT_BRAND: BrandPalette = {
  sidebarApp: "linear-gradient(180deg, #C9A368 0%, #C0985D 55%, #B68F54 100%)",
  sidebarBorder: "transparent",
  sidebarAdmin: "linear-gradient(180deg, #5E4C3D 0%, #4F3F32 55%, #3F3126 100%)",
  authPanel: "linear-gradient(150deg, #6B5746 0%, #5D4A3C 55%, #4A3A2E 100%)",
  heroCard: "linear-gradient(135deg, #5D4A3C 0%, #4A3A2E 100%)",
  heroCardBorder: "transparent",
  cream: "#FCF6EA",
  creamStroke: "#E4D3B2",
  surface2: "#FBF8F2",
  surfaceHover: "#FBF8F2",
  hexTileBg: "#FCF6EA",
  hexTileStroke: "#E4D3B2",
  navActiveBg: "rgba(255,255,255,0.24)",
  navActiveText: "#FFFFFF",
  inputFocusHalo: alpha(GOLD, 0.15),
  successBg: "#EAF7EE",
  warningBg: "#FBF3DC",
  errorBg: "rgba(255,72,66,0.12)",
  whatsapp: "#25D366",
  topbar: "#FFFFFF",
  switcherBg: "rgba(0,0,0,0.12)",
  switcherActiveBg: "#FFFFFF",
  switcherActiveText: "#B0884D",
  navGroupLabel: "rgba(255,255,255,0.55)",
  navItemText: "rgba(255,255,255,0.82)",
  navHoverBg: "rgba(255,255,255,0.14)",
  badgeIaBg: "rgba(255,255,255,0.22)",
  badgeIaText: "#FFFFFF",
  heroBg: "linear-gradient(115deg, #FCF6EA 0%, #F7EEDC 100%)",
  heroBorder: "#EFE3CC",
  heroText: "#6B5B47",
  heroMobile: "linear-gradient(120deg, #C9A368, #B68F54)",
  stepDoneBg: "#E3CFA8",
  stepDoneText: "#7A6038",
  toggleOff: "#E0D7C6",
};

const DARK_BRAND: BrandPalette = {
  sidebarApp: "#211C17",
  sidebarBorder: "#322B23",
  sidebarAdmin: "linear-gradient(180deg, #221D18 0%, #1C1813 55%, #15110C 100%)",
  authPanel: "linear-gradient(150deg, #463729 0%, #322619 55%, #20180F 100%)",
  heroCard: "linear-gradient(135deg, #322619 0%, #241B10 100%)",
  heroCardBorder: "#463722",
  cream: alpha(GOLD_DARKMODE, 0.12),
  creamStroke: "transparent",
  surface2: "#1E1915",
  surfaceHover: "#2C2620",
  hexTileBg: alpha(GOLD_DARKMODE, 0.12),
  hexTileStroke: "transparent",
  navActiveBg: alpha(GOLD_DARKMODE, 0.16),
  navActiveText: "#E8D6B6",
  inputFocusHalo: alpha(GOLD_DARKMODE, 0.18),
  successBg: "rgba(111,191,142,0.14)",
  warningBg: "rgba(216,180,104,0.14)",
  errorBg: "rgba(255,72,66,0.16)",
  whatsapp: "#25D366",
  topbar: "#1A1714",
  switcherBg: "rgba(0,0,0,0.25)",
  switcherActiveBg: GOLD_DARKMODE,
  switcherActiveText: "#1A1714",
  navGroupLabel: "#938670",
  navItemText: "rgba(243,237,226,0.78)",
  navHoverBg: alpha(GOLD_DARKMODE, 0.1),
  badgeIaBg: alpha(GOLD_DARKMODE, 0.2),
  badgeIaText: GOLD_DARKMODE,
  heroBg: "linear-gradient(115deg, #2A2118 0%, #322619 100%)",
  heroBorder: "#3E3220",
  heroText: "#BCAE98",
  heroMobile: "linear-gradient(120deg, #C9A368, #B68F54)",
  stepDoneBg: alpha(GOLD_DARKMODE, 0.22),
  stepDoneText: "#D8BE8F",
  toggleOff: "#463E33",
};

export const lightPalette = {
  mode: "light" as const,
  common: { black: "#000", white: "#fff" },
  primary: {
    lighter: "#FCF6EA",
    light: "#D1BB8E",
    main: GOLD,
    dark: "#B0884D",
    darker: "#8F6F3F",
    contrastText: "#fff",
  },
  secondary: {
    lighter: "#8A7B68",
    light: "#6B5B47",
    main: "#5D4A3C",
    dark: "#4A3A2E",
    darker: "#3F3126",
    contrastText: "#fff",
  },
  info: {
    lighter: "#d1bb8e",
    light: "#d1bb8e",
    main: "#d1bb8e",
    dark: "#d1bb8e",
    darker: "#d1bb8e",
    contrastText: "#fff",
  },
  success: {
    lighter: "#EAF7EE",
    light: "#7FC295",
    main: "#54A86B",
    dark: "#3E8A54",
    darker: "#2E6B40",
    contrastText: "#fff",
  },
  warning: {
    lighter: "#FBF3DC",
    light: "#D8B468",
    main: "#B6912E",
    dark: "#97761F",
    darker: "#785D14",
    contrastText: "#fff",
  },
  error: {
    lighter: "#FFE7D9",
    light: "#FFA48D",
    main: "#FF4842",
    dark: "#B72136",
    darker: "#7A0C2E",
    contrastText: "#fff",
  },
  grey: GREY,
  gradients: GRADIENTS,
  brand: LIGHT_BRAND,
  divider: "#EEE7D9",
  text: { primary: "#2A2118", secondary: "#8A7B68", disabled: "#9A8B76" },
  // background.default é espelhado no anti-FOUC do public/index.html (manter em sincronia)
  background: { paper: "#FFFFFF", default: "#F6F3EE", neutral: "#FBF8F2" },
  action: {
    active: GREY[600],
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export type AppPalette = Omit<typeof lightPalette, "mode"> & { mode: "light" | "dark" };

export const darkPalette: AppPalette = {
  mode: "dark",
  common: { black: "#000", white: "#fff" },
  primary: {
    lighter: alpha(GOLD_DARKMODE, 0.16),
    light: "#D9BC8B",
    main: GOLD_DARKMODE,
    dark: "#B8924F",
    darker: "#A07F3E",
    contrastText: "#1A1714",
  },
  secondary: {
    lighter: "#A89B86",
    light: "#8A7B68",
    main: "#6B5B47",
    dark: "#5D4A3C",
    darker: "#4A3A2E",
    contrastText: "#F3EDE2",
  },
  info: {
    lighter: "#d1bb8e",
    light: "#d1bb8e",
    main: "#d1bb8e",
    dark: "#d1bb8e",
    darker: "#d1bb8e",
    contrastText: "#1A1714",
  },
  success: {
    lighter: "rgba(111,191,142,0.14)",
    light: "#8FCFA6",
    main: "#6FBF8E",
    dark: "#57A476",
    darker: "#3E8A54",
    contrastText: "#14110E",
  },
  warning: {
    lighter: "rgba(216,180,104,0.14)",
    light: "#E2C685",
    main: "#D8B468",
    dark: "#B6912E",
    darker: "#97761F",
    contrastText: "#14110E",
  },
  error: {
    lighter: "rgba(255,72,66,0.16)",
    light: "#FFA48D",
    main: "#FF4842",
    dark: "#B72136",
    darker: "#7A0C2E",
    contrastText: "#fff",
  },
  grey: GREY,
  gradients: GRADIENTS,
  brand: DARK_BRAND,
  divider: "#322B23",
  text: {
    primary: "#F3EDE2",
    secondary: "#B5A892",
    disabled: alpha("#F3EDE2", 0.4),
  },
  // background.default é espelhado no anti-FOUC do public/index.html (manter em sincronia)
  background: { paper: "#211C17", default: "#14110E", neutral: "#1E1915" },
  action: {
    active: "#9C8F7A",
    hover: alpha(GOLD_DARKMODE, 0.08),
    selected: alpha(GOLD_DARKMODE, 0.16),
    disabled: alpha("#F3EDE2", 0.3),
    disabledBackground: alpha("#F3EDE2", 0.12),
    focus: alpha(GOLD_DARKMODE, 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

/**
 * Compat legado: o export default é o modo CLARO, estático.
 * Código novo deve usar useTheme()/sx — nunca importar este default.
 */
const palette = lightPalette;

export default palette;
